import { useAppDispatch, useAppSelector } from '@casops/store/hooks';
import { closeSidePanel, SidePanel } from '@casops/store/slices/globalSlice';
import { useCallback, useMemo } from 'react';
import UserSettingsPanel from './user-settings-panel/UserSettingsPanel';
import ManageUser from './manage-user/ManageUser';
import ManageGeofenceArea from './manage-geofence-area/ManageGeofenceArea';
import ManageGeofenceStyle from './manage-geofence-style/ManageGeofenceStyle';
import ManageGeofenceSpeedLimit from './manage-geofence-speed-limit/ManageGeofenceSpeedLimit';
import ManageGeofenceHoldTime from './manage-geofence-hold-time/ManageGeofenceHoldTime';
import ManageAssetGroup from './manage-asset-group/ManageAssetGroup';
import ManageConfigurationGroup from './manage-configuration-group/ManageConfigurationGroup';
import ManageSettingsPanel from './manage-settings-panel/ManageSettingsPanel';
import AssetsAssignConfigurationGroupPanel from './assets-assign-configuration-group-panel/AssetsAssignConfigurationGroupPanel';
import FilterPanel from './filter-panel/FilterPanel';
import { resetDraftFilters } from '@casops/store/slices/filtersSlice';
import LibraryUploadPanel from './library-upload-panel/LibraryUploadPanel';
import CertificateImportPanel from './certificate-import-panel/CertificateImportPanel';
import PreApprovalImportPanel from './pre-aproval-import-panel/PreApprovalImportPanel';
import ManageAssetApproval from './manage-asset-approval/ManageAssetApproval';
import ManageAsset from './manage-asset/ManageAsset';
import DeviceFileUploadPanel from './device-file-upload-panel/DeviceFileUploadPanel';
import ReadGeofence from './read-geofence/ReadGeofence';
import ManageConfigurationGroupPackage from './manage-configuration-group-package/ManageConfigurationGroupPackage';
import UserChangePasswordPanel from './user-change-password-panel/UserChangePasswordPanel';
import AssetTracksPanel from './asset-tracks-panel/AssetTracksPanel';
import GeofenceImportPanel from './goefence-import-panel/GeofenceImportPanel';
import SafetyAlertPanel from './safety-alert/SafetyAlertPanel';
import ManageSafetySettings from './manage-safety-settings-panel/ManageSafetySettings';
import ManagePinDropTemplate from './manage-pin-drop-template/ManagePinDropTemplate';

const useSidePanel = () => {
  const dispatch = useAppDispatch();

  const { sidePanelOpened, sidePanel, sidePanelFilters } = useAppSelector(state => state.global);

  const onDrawerClosed = useCallback(() => {
    dispatch(closeSidePanel());
    dispatch(resetDraftFilters());
  }, [dispatch]);

  const activePanel = useMemo(() => {
    if (sidePanelOpened) {
      switch (sidePanel) {
        case SidePanel.ManageUser:
          return <ManageUser />;

        case SidePanel.ManageAssetGroup:
          return <ManageAssetGroup />;

        case SidePanel.ManageAsset:
          return <ManageAsset />;

        case SidePanel.ManageConfigurationGroup:
          return <ManageConfigurationGroup />;

        case SidePanel.ManageConfigurationGroupPackage:
          return <ManageConfigurationGroupPackage />;

        case SidePanel.ManageGeofenceArea:
          return <ManageGeofenceArea />;

        case SidePanel.ManageGeofenceStyle:
          return <ManageGeofenceStyle />;

        case SidePanel.ReadGeofence:
          return <ReadGeofence />;

        case SidePanel.ManageGeofenceSpeedLimit:
          return <ManageGeofenceSpeedLimit />;

        case SidePanel.ManageGeofenceHoldTime:
          return <ManageGeofenceHoldTime />;

        case SidePanel.ManagePinDropTemplate:
          return <ManagePinDropTemplate />;

        case SidePanel.CertificateImport:
          return <CertificateImportPanel />;

        case SidePanel.LibraryUpload:
          return <LibraryUploadPanel />;

        case SidePanel.DeviceFileUpload:
          return <DeviceFileUploadPanel />;

        case SidePanel.PreApprovalImport:
          return <PreApprovalImportPanel />;

        case SidePanel.ManageAssetApproval:
          return <ManageAssetApproval />;

        case SidePanel.UserSettings:
          return <UserSettingsPanel />;

        case SidePanel.UserChangePassword:
          return <UserChangePasswordPanel />;

        case SidePanel.Settings:
          return <ManageSettingsPanel />;

        case SidePanel.AssetTracks:
          return <AssetTracksPanel />;

        case SidePanel.AssetsAssignConfigurationGroup:
          return <AssetsAssignConfigurationGroupPanel />;

        case SidePanel.GeofenceImport:
          return <GeofenceImportPanel />;

        case SidePanel.SafetyAlert:
          return <SafetyAlertPanel />;

        case SidePanel.ManagePersonnelSafety:
          return <ManageSafetySettings />;

        case SidePanel.Filters:
          if (sidePanelFilters) {
            return <FilterPanel filterGroup={sidePanelFilters} />;
          }
      }
    }

    return null;
  }, [sidePanelOpened, sidePanel, sidePanelFilters]);

  return {
    isDrawerOpen: sidePanelOpened ?? false,
    onDrawerClosed,
    activePanel,
  };
};

export default useSidePanel;
